import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {calculateRemSize, mb, p, pb} from "@pg-design/helpers-css";
import {BrandCheckboxCheckedIcon, BrandGuideIcon, DownloadIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {CheckboxControlled, InputField} from "@pg-design/rhf-components-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {rpAppLink} from "@pg-mono/rp-routes";
import {userComAttachmentDownload} from "@pg-mono/user-com";

import {IRPStore} from "../../../app/rp_reducer";
import {LeadRodoText} from "../../../application/components/LeadRodoText";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useGetArticleAttachmentMutation} from "../../api/get_article";
import {useArticleAttachmentsLSStore} from "../../hooks/use_article_attachment_ls_store";

const schema = Yup.object().shape({
    email: Yup.string().email().required(),
    optIn: Yup.boolean().required().oneOf([true], "Zgoda jest wymagana")
});

interface Props {
    attachmentId: number;
    attachmentLink: string;
    title: string;
}

export const ArticleAttachmentModalButton = (props: Props) => {
    const [modalPage, setModalPage] = useState<"form" | "success" | null>(null);

    // get available user data
    const {data: profileData} = useGetUserProfileQuery();
    const optinFromProfile = profileData?.email_marketing_consent;
    const emailFromProfile = profileData?.email;

    const sentLeadFormValues = useSelector((state: IRPStore) => state.sentLead.storedValues);
    const emailFromStorage = sentLeadFormValues?.email;

    const userEmail = emailFromStorage || emailFromProfile;

    const {email: articleAttachmentSavedEmail, storeEmail} = useArticleAttachmentsLSStore();
    const isDownloadAllowed = (userEmail && optinFromProfile) || articleAttachmentSavedEmail;

    // form setup
    const defaultFormValuesFromProfile = {
        email: userEmail,
        optIn: optinFromProfile
    };

    const methods = useFormRHF<{email: string; optIn: boolean}>({
        defaultValues: defaultFormValuesFromProfile,
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        if (defaultFormValuesFromProfile.email && methods.getValues("email") !== defaultFormValuesFromProfile.email) {
            methods.setValue("email", defaultFormValuesFromProfile.email);
        }
    }, [defaultFormValuesFromProfile]);

    // callbacks
    const onDownload = () => {
        if ((defaultFormValuesFromProfile.email && defaultFormValuesFromProfile.optIn) || isDownloadAllowed) {
            userComAttachmentDownload({
                email: methods.getValues("email"),
                attachmentTitle: props.title
            });

            window.open(props.attachmentLink);
            return;
        }
        setModalPage("form");
    };

    const onSecondStepDownload = () => {
        setModalPage(null);
    };

    const {handleSubmit} = methods;
    const [trigger, {data, isLoading}] = useGetArticleAttachmentMutation();
    const onSubmit = async () => {
        await trigger({
            attachment_id: props.attachmentId,
            email: methods.getValues("email"),
            has_email_marketing_consent: methods.getValues("optIn")
        })
            .unwrap()
            .then(() => {
                storeEmail(methods.getValues("email"));

                userComAttachmentDownload({
                    email: methods.getValues("email"),
                    attachmentTitle: props.title,
                    emailOptIn: methods.getValues("optIn")
                });

                setModalPage("success");
            })
            .catch((error) => {
                notifyBugsnag(error, "useGetArticleAttachmentMutation");
            });
    };

    return (
        <>
            <div onClick={onDownload} css={downloadItem}>
                <Text as="div" variant="body_copy_2">
                    {props.title}
                </Text>
                <DownloadIcon size="2.4" wrapperSize="4.4" wrapperColor={getThemeVariable("colors.primary")} css={showOnMobile} />
                <Button iconLeft={DownloadIcon} variant="filled_primary" css={showOnDesktop} type="button">
                    Pobierz
                </Button>
            </div>
            {Boolean(modalPage) && (
                <Modal isOpen={Boolean(modalPage)} onModalClose={() => setModalPage(null)} modalTheme="light">
                    {modalPage === "form" ? (
                        <>
                            <div css={modalHeadingStyle}>
                                <BrandGuideIcon size="9" wrapperSize="10" wrapperType="circle" wrapperColor={getThemeVariable("colors.primary")} />
                                <div css={p(2)}>
                                    <Text variant="headline_3">Pobierz darmowy materiał</Text>
                                    <Text variant="body_copy_2">Poszerz swoją wiedzę o zakupie mieszkania</Text>
                                </div>
                                <Modal.CloseButton placement="right" />
                            </div>
                            <Modal.Content css={modalStyle}>
                                <FormProviderRHF {...methods}>
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <InputField name="email" placeholder="e-mail" css={pb(2)} disabled={Boolean(emailFromProfile)} />
                                        <CheckboxControlled
                                            id="optInCheckbox"
                                            name="optIn"
                                            labelContent="Chcę otrzymywać mailem linki do nowych ofert nieruchomości. Zgodę mogę zawsze odwołać."
                                            checkedMarkColor={getThemeVariable("globals.checkbox.checkedColor")}
                                            css={mb(8)}
                                        />
                                        <Text variant="info_txt_2" align="center" color={getThemeVariable("colors.gray.700")} css={termsStyle}>
                                            Akceptuję{" "}
                                            <a href={rpAppLink.termsOfService.base()} target="_blank">
                                                regulamin serwisu
                                            </a>
                                        </Text>
                                        <Button variant="filled_primary" css={mb(2)} fullWidth isLoading={isLoading}>
                                            pobierz
                                        </Button>
                                        <LeadRodoText
                                            privacyPolicyUrl={rpAppLink.rodoTerms.base()}
                                            vendorName="Property Group sp. z o.o."
                                            privacyDataProcessorName={null}
                                            align="center"
                                        />
                                    </form>
                                </FormProviderRHF>
                            </Modal.Content>
                        </>
                    ) : (
                        <>
                            <BrandCheckboxCheckedIcon
                                css={successIconStyle}
                                size="9"
                                wrapperSize="10"
                                wrapperType="circle"
                                wrapperColor={getThemeVariable("colors.primary")}
                            />
                            <Modal.Content css={modalStyle}>
                                <div css={successTextWrapper}>
                                    <Text variant="headline_3" align="center">
                                        Materiał jest gotowy do pobrania
                                    </Text>
                                    <Text variant="body_copy_2" align="center">
                                        Wysłaliśmy także na Twój adres e-mail
                                    </Text>
                                </div>
                                <Button
                                    href={data?.attachment_url}
                                    target="_blank"
                                    variant="filled_primary"
                                    css={mb(2)}
                                    iconLeft={DownloadIcon}
                                    onClick={onSecondStepDownload}
                                >
                                    pobierz
                                </Button>
                            </Modal.Content>
                        </>
                    )}
                </Modal>
            )}
        </>
    );
};

const downloadItem = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const modalStyle = css`
    ${p(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(3)};

        width: 556px;
    }
`;

const modalHeadingStyle = css`
    ${p(3)};
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const termsStyle = css`
    ${mb(2)}

    a,
    a:hover,
    a:visited,
    a:active {
        color: inherit;
        text-decoration: underline;
    }
`;

const showOnDesktop = css`
    display: none;

    @media (min-width: ${getThemeBreakpoint().md}) {
        display: block;
    }
`;

const showOnMobile = css`
    display: flex;

    @media (min-width: ${getThemeBreakpoint().md}) {
        display: none;
    }
`;

const successTextWrapper = css`
    ${p(2)};
    ${mb(2)};
`;

const successIconStyle = css`
    margin: ${calculateRemSize(2)} auto 0 auto;
`;
import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {calculateRemSize, mb, p, pb} from "@pg-design/helpers-css";
import {BrandCheckboxCheckedIcon, BrandGuideIcon, DownloadIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {CheckboxControlled, InputField} from "@pg-design/rhf-components-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {Text} from "@pg-design/text-module";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import {rpAppLink} from "@pg-mono/rp-routes";
import {userComAttachmentDownload} from "@pg-mono/user-com";

import {IRPStore} from "../../../app/rp_reducer";
import {LeadRodoText} from "../../../application/components/LeadRodoText";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useGetArticleAttachmentMutation} from "../../api/get_article";
import {useArticleAttachmentsLSStore} from "../../hooks/use_article_attachment_ls_store";

const schema = Yup.object().shape({
    email: Yup.string().email().required(),
    optIn: Yup.boolean().required().oneOf([true], "Zgoda jest wymagana")
});

interface Props {
    attachmentId: number;
    attachmentLink: string;
    title: string;
}

export const ArticleAttachmentModalButton = (props: Props) => {
    const [modalPage, setModalPage] = useState<"form" | "success" | null>(null);

    // get available user data
    const {data: profileData} = useGetUserProfileQuery();
    const optinFromProfile = profileData?.email_marketing_consent;
    const emailFromProfile = profileData?.email;

    const sentLeadFormValues = useSelector((state: IRPStore) => state.sentLead.storedValues);
    const emailFromStorage = sentLeadFormValues?.email;

    const userEmail = emailFromStorage || emailFromProfile;

    const {email: articleAttachmentSavedEmail, storeEmail} = useArticleAttachmentsLSStore();
    const isDownloadAllowed = (userEmail && optinFromProfile) || articleAttachmentSavedEmail;

    // form setup
    const defaultFormValuesFromProfile = {
        email: userEmail,
        optIn: optinFromProfile
    };

    const methods = useFormRHF<{email: string; optIn: boolean}>({
        defaultValues: defaultFormValuesFromProfile,
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        if (defaultFormValuesFromProfile.email && methods.getValues("email") !== defaultFormValuesFromProfile.email) {
            methods.setValue("email", defaultFormValuesFromProfile.email);
        }
    }, [defaultFormValuesFromProfile]);

    // callbacks
    const onDownload = () => {
        if ((defaultFormValuesFromProfile.email && defaultFormValuesFromProfile.optIn) || isDownloadAllowed) {
            userComAttachmentDownload({
                email: methods.getValues("email"),
                attachmentTitle: props.title
            });

            window.open(props.attachmentLink);
            return;
        }
        setModalPage("form");
    };

    const onSecondStepDownload = () => {
        setModalPage(null);
    };

    const {handleSubmit} = methods;
    const [trigger, {data, isLoading}] = useGetArticleAttachmentMutation();
    const onSubmit = async () => {
        await trigger({
            attachment_id: props.attachmentId,
            email: methods.getValues("email"),
            has_email_marketing_consent: methods.getValues("optIn")
        })
            .unwrap()
            .then(() => {
                storeEmail(methods.getValues("email"));

                userComAttachmentDownload({
                    email: methods.getValues("email"),
                    attachmentTitle: props.title,
                    emailOptIn: methods.getValues("optIn")
                });

                setModalPage("success");
            })
            .catch((error) => {
                notifyBugsnag(error, "useGetArticleAttachmentMutation");
            });
    };

    return (
        <>
            <div onClick={onDownload} css={downloadItem}>
                <Text as="div" variant="body_copy_2">
                    {props.title}
                </Text>
                <DownloadIcon size="2.4" wrapperSize="4.4" wrapperColor={getThemeVariable("colors.primary")} css={showOnMobile} />
                <Button iconLeft={DownloadIcon} variant="filled_primary" css={showOnDesktop} type="button">
                    Pobierz
                </Button>
            </div>
            {Boolean(modalPage) && (
                <Modal isOpen={Boolean(modalPage)} onModalClose={() => setModalPage(null)} modalTheme="light">
                    {modalPage === "form" ? (
                        <>
                            <div css={modalHeadingStyle}>
                                <BrandGuideIcon size="9" wrapperSize="10" wrapperType="circle" wrapperColor={getThemeVariable("colors.primary")} />
                                <div css={p(2)}>
                                    <Text variant="headline_3">Pobierz darmowy materiał</Text>
                                    <Text variant="body_copy_2">Poszerz swoją wiedzę o zakupie mieszkania</Text>
                                </div>
                                <Modal.CloseButton placement="right" />
                            </div>
                            <Modal.Content css={modalStyle}>
                                <FormProviderRHF {...methods}>
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <InputField name="email" placeholder="e-mail" css={pb(2)} disabled={Boolean(emailFromProfile)} />
                                        <CheckboxControlled
                                            id="optInCheckbox"
                                            name="optIn"
                                            labelContent="Chcę otrzymywać mailem linki do nowych ofert nieruchomości. Zgodę mogę zawsze odwołać."
                                            checkedMarkColor={getThemeVariable("globals.checkbox.checkedColor")}
                                            css={mb(8)}
                                        />
                                        <Text variant="info_txt_2" align="center" color={getThemeVariable("colors.gray.700")} css={termsStyle}>
                                            Akceptuję{" "}
                                            <a href={rpAppLink.termsOfService.base()} target="_blank">
                                                regulamin serwisu
                                            </a>
                                        </Text>
                                        <Button variant="filled_primary" css={mb(2)} fullWidth isLoading={isLoading}>
                                            pobierz
                                        </Button>
                                        <LeadRodoText
                                            privacyPolicyUrl={rpAppLink.rodoTerms.base()}
                                            vendorName="Property Group sp. z o.o."
                                            privacyDataProcessorName={null}
                                            align="center"
                                        />
                                    </form>
                                </FormProviderRHF>
                            </Modal.Content>
                        </>
                    ) : (
                        <>
                            <BrandCheckboxCheckedIcon
                                css={successIconStyle}
                                size="9"
                                wrapperSize="10"
                                wrapperType="circle"
                                wrapperColor={getThemeVariable("colors.primary")}
                            />
                            <Modal.Content css={modalStyle}>
                                <div css={successTextWrapper}>
                                    <Text variant="headline_3" align="center">
                                        Materiał jest gotowy do pobrania
                                    </Text>
                                    <Text variant="body_copy_2" align="center">
                                        Wysłaliśmy także na Twój adres e-mail
                                    </Text>
                                </div>
                                <Button
                                    href={data?.attachment_url}
                                    target="_blank"
                                    variant="filled_primary"
                                    css={mb(2)}
                                    iconLeft={DownloadIcon}
                                    onClick={onSecondStepDownload}
                                >
                                    pobierz
                                </Button>
                            </Modal.Content>
                        </>
                    )}
                </Modal>
            )}
        </>
    );
};

const downloadItem = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;

const modalStyle = css`
    ${p(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(3)};

        width: 556px;
    }
`;

const modalHeadingStyle = css`
    ${p(3)};
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const termsStyle = css`
    ${mb(2)}

    a,
    a:hover,
    a:visited,
    a:active {
        color: inherit;
        text-decoration: underline;
    }
`;

const showOnDesktop = css`
    display: none;

    @media (min-width: ${getThemeBreakpoint().md}) {
        display: block;
    }
`;

const showOnMobile = css`
    display: flex;

    @media (min-width: ${getThemeBreakpoint().md}) {
        display: none;
    }
`;

const successTextWrapper = css`
    ${p(2)};
    ${mb(2)};
`;

const successIconStyle = css`
    margin: ${calculateRemSize(2)} auto 0 auto;
`;
